/**
 * Cart Donation Bar - Live update functionality
 * Updates meal count with animation when cart changes
 * 
 * Logic: 1 meal per 2 items in cart, maximum 3 meals, with optional multiplier
 */

class CartDonationBar {
  constructor() {
    this.bar = document.querySelector('[data-donation-bar]');
    this.countElement = document.querySelector('[data-donation-count]');
    this.labelElement = document.querySelector('[data-donation-label]');
    this.currentCount = this.countElement ? parseInt(this.countElement.textContent) || 0 : 0;
    this.multiplier = this.bar ? parseInt(this.bar.dataset.multiplier) || 1 : 1;
  }

  /**
   * Update the donation count with animation
   * @param {number} newCount - The new meal count (already calculated with multiplier)
   */
  updateCount(newCount) {
    if (!this.countElement) {
      this.countElement = document.querySelector('[data-donation-count]');
      if (!this.countElement) return;
    }

    const oldCount = this.currentCount;
    
    if (newCount === oldCount) return;

    // Add animation class
    this.countElement.classList.add('is-updating');

    // Animate the number change
    this.animateValue(oldCount, newCount, 300);

    // Update the label (MEAL vs MEALS)
    this.updateLabel(newCount);

    // Remove animation class after animation completes
    setTimeout(() => {
      this.countElement.classList.remove('is-updating');
    }, 400);

    this.currentCount = newCount;
  }

  /**
   * Update the label based on count (singular vs plural)
   * @param {number} count - The meal count
   */
  updateLabel(count) {
    if (!this.labelElement) {
      this.labelElement = document.querySelector('[data-donation-label]');
    }
    if (this.labelElement) {
      this.labelElement.textContent = count === 1 ? 'MEAL' : 'MEALS';
    }
  }

  /**
   * Animate number from start to end value
   * @param {number} start - Starting value
   * @param {number} end - Ending value  
   * @param {number} duration - Animation duration in ms
   */
  animateValue(start, end, duration) {
    const range = end - start;
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.round(start + (range * easeOutQuart));
      
      if (this.countElement) {
        this.countElement.textContent = currentValue;
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }

  /**
   * Calculate total meals from item count (with multiplier)
   * Logic: 1 meal per 2 items, maximum 3 meals, then apply multiplier
   * @param {number} itemCount - Total number of items in cart
   * @param {number} multiplier - Donation multiplier (default: 1)
   * @returns {number} Total meal count
   */
  static calculateMealsFromItemCount(itemCount, multiplier = 1) {
    if (!itemCount || itemCount < 1) return 0;
    const baseMeals = Math.ceil(itemCount / 2);
    const cappedBaseMeals = Math.min(baseMeals, 3); // Cap base at 3 meals
    const totalMeals = cappedBaseMeals * multiplier;
    return Math.min(totalMeals, 3 * multiplier); // Cap at max meals with multiplier
  }

  /**
   * Calculate total meals from cart items array
   * @param {Array} items - Cart items array
   * @param {number} multiplier - Donation multiplier (default: 1)
   * @returns {number} Total meal count
   */
  static calculateMeals(items, multiplier = 1) {
    if (!items || !Array.isArray(items)) return 0;
    const totalItems = items.reduce((total, item) => total + item.quantity, 0);
    return CartDonationBar.calculateMealsFromItemCount(totalItems, multiplier);
  }

  /**
   * Refresh the bar element references (useful after DOM updates)
   */
  refresh() {
    this.bar = document.querySelector('[data-donation-bar]');
    this.countElement = document.querySelector('[data-donation-count]');
    this.labelElement = document.querySelector('[data-donation-label]');
    if (this.bar) {
      this.multiplier = parseInt(this.bar.dataset.multiplier) || 1;
    }
    if (this.countElement) {
      this.currentCount = parseInt(this.countElement.textContent) || 0;
    }
  }

  /**
   * Get the current multiplier
   * @returns {number} Current multiplier value
   */
  getMultiplier() {
    return this.multiplier;
  }
}

// Initialize global instance
window.cartDonationBar = new CartDonationBar();

/**
 * Global function to update donation bar
 * Can be called from cart.js after cart updates
 * @param {number|Array} countOrItems - Either the item count or cart items array
 */
function updateDonationBar(countOrItems) {
  if (!window.cartDonationBar) {
    window.cartDonationBar = new CartDonationBar();
  }
  
  // Refresh element references in case DOM was updated
  window.cartDonationBar.refresh();
  
  const multiplier = window.cartDonationBar.getMultiplier();
  let newMealCount;
  
  if (Array.isArray(countOrItems)) {
    // If passed an array of items, calculate meals from it
    newMealCount = CartDonationBar.calculateMeals(countOrItems, multiplier);
  } else {
    // If passed a number, treat it as item count and calculate meals
    const itemCount = parseInt(countOrItems) || 0;
    newMealCount = CartDonationBar.calculateMealsFromItemCount(itemCount, multiplier);
  }
  
  window.cartDonationBar.updateCount(newMealCount);
}

// Make function globally available
window.updateDonationBar = updateDonationBar;

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartDonationBar = new CartDonationBar();
});